import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DoCheck,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FieldArrayType, FormlyFieldConfig } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

/**
 * Configuration interface for the CustomDynamicRepeaterComponent
 */
export interface CustomDynamicRepeaterConfig {
  /** Field name in API data for item ID (supports dot notation) */
  itemIdField?: string;
  /** Field name in API data for item label/display text (supports dot notation) */
  itemLabelField?: string;
  /** Message to show when no items are available */
  emptyStateMessage?: string;
  /** Whether to show debug information in console */
  enableDebugLogging?: boolean;
}

@Component({
  standalone: false,
  selector: 'irembogov-custom-dynamic-repeater',
  templateUrl: './custom-dynamic-repeater.component.html',
  styleUrls: ['./custom-dynamic-repeater.component.scss'],
})
export class CustomDynamicRepeaterComponent
  extends FieldArrayType
  implements OnInit, AfterViewInit, OnDestroy, DoCheck
{
  private readonly subscriptions = new Subscription();
  private originalFieldGroup: FormlyFieldConfig[] = [];
  private previousItemsRef: any[] = [];

  /** Component configuration merged with defaults */
  config: CustomDynamicRepeaterConfig = {};

  /** Items data from props */
  itemsData: any[] = [];

  /** Default configuration values */
  private readonly defaultConfig: CustomDynamicRepeaterConfig = {
    itemIdField: 'id',
    itemLabelField: 'label',
    emptyStateMessage: 'No items available',
    enableDebugLogging: false,
  };

  constructor(
    private readonly cd: ChangeDetectorRef,
    private readonly translateService: TranslateService
  ) {
    super();
  }

  ngOnInit(): void {
    this.initializeConfiguration();
    this.storeOriginalFieldGroup();
    this.enhanceTemplateWithHiddenFields();
    this.initializeItemsData();
    this.showTemplateFieldsImmediately();

    // Initialize model property if it doesn't exist to prevent expression errors
    this.initializeModelProperty();

    this.debugLog('Component initialized', {
      fieldKey: this.field.key,
      config: this.config,
      originalFieldGroup: this.originalFieldGroup,
      itemsData: this.itemsData,
    });
  }

  ngAfterViewInit(): void {
    this.cd.detectChanges();
  }

  ngDoCheck(): void {
    const newItems = this.field.props?.['items'] ?? [];

    // Add safety check for expressions that might return non-array values
    if (!Array.isArray(newItems) && newItems !== this.previousItemsRef) {
      this.debugLog('Invalid items data type, using empty array', {
        receivedType: typeof newItems,
        receivedValue: newItems,
      });
      this.field.props = this.field.props || {};
      this.field.props['items'] = [];
      return;
    }

    // Detect deep array changes like the table component
    if (!Array.isArray(newItems)) return;

    if (newItems !== this.previousItemsRef) {
      this.previousItemsRef = newItems;
      this.itemsData = newItems;
      this.processItemsData();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Initialize component configuration by merging props with defaults
   */
  private initializeConfiguration(): void {
    this.config = {
      ...this.defaultConfig,
      ...(this.field.props?.['dynamicRepeaterConfig'] || {}),
    };

    this.debugLog('Configuration initialized', {
      defaultConfig: this.defaultConfig,
      propsConfig: this.field.props?.['dynamicRepeaterConfig'],
      finalConfig: this.config,
    });
  }

  /**
   * Store the original fieldGroup template before enhancement
   */
  private storeOriginalFieldGroup(): void {
    const fieldArray = this.field.fieldArray;
    if (
      fieldArray &&
      typeof fieldArray === 'object' &&
      'fieldGroup' in fieldArray
    ) {
      this.originalFieldGroup = fieldArray.fieldGroup
        ? JSON.parse(JSON.stringify(fieldArray.fieldGroup))
        : [];
    } else {
      this.originalFieldGroup = [];
    }
  }

  /**
   * Enhance the template with hidden tracking fields
   */
  private enhanceTemplateWithHiddenFields(): void {
    const fieldArray = this.field.fieldArray;
    if (
      !fieldArray ||
      typeof fieldArray !== 'object' ||
      !('fieldGroup' in fieldArray)
    ) {
      this.field.fieldArray = { fieldGroup: [] };
    }

    const enhancedFieldArray = this.field.fieldArray as {
      fieldGroup: FormlyFieldConfig[];
    };

    // Check if hidden fields are already present to avoid duplication
    const existingKeys = enhancedFieldArray.fieldGroup.map(field => field.key);
    const hiddenFieldKeys = ['itemId', 'itemLabel', '_isApiPopulated'];
    const hasHiddenFields = hiddenFieldKeys.some(key =>
      existingKeys.includes(key)
    );

    if (hasHiddenFields) {
      this.debugLog('Hidden fields already exist, skipping enhancement', {
        existingKeys,
        fieldGroup: enhancedFieldArray.fieldGroup,
      });
      return;
    }

    const hiddenFields: FormlyFieldConfig[] = [
      {
        key: 'itemId',
        type: 'input',
        props: {
          hideField: true,
        },
        defaultValue: null, // Ensure it starts with a default value
      },
      {
        key: '_isApiPopulated',
        type: 'input',
        props: {
          hideField: true,
        },
        defaultValue: false,
      },
    ];

    // Prepend hidden fields to the template
    enhancedFieldArray.fieldGroup = [
      ...hiddenFields,
      ...this.originalFieldGroup,
    ];

    this.debugLog('Template enhanced with hidden fields', {
      enhancedFieldGroup: enhancedFieldArray.fieldGroup,
    });
  }

  /**
   * Initialize model property to prevent expression errors
   * This ensures the model property exists even before API data arrives
   */
  private initializeModelProperty(): void {
    if (this.field.key && this.model && !this.model[this.field.key as string]) {
      // Initialize as empty array to prevent undefined errors in expressions
      this.model[this.field.key as string] = [];

      this.debugLog('Initialized model property', {
        key: this.field.key,
        initialValue: [],
      });
    }
  }

  /**
   * Initialize items data from props
   */
  private initializeItemsData(): void {
    this.itemsData = Array.isArray(this.field.props?.['items'])
      ? this.field.props['items']
      : [];
    this.previousItemsRef = this.itemsData;

    this.debugLog('Items data initialized', {
      itemsCount: this.itemsData.length,
      items: this.itemsData,
    });
  }

  /**
   * Show template fields only when API data is available
   * Dynamic repeater should NOT show any fields initially
   */
  private showTemplateFieldsImmediately(): void {
    // Only process if we have actual items data from API
    if (this.itemsData.length > 0) {
      this.processItemsData();
      this.debugLog('Template fields shown with API data', {
        itemCount: this.itemsData.length,
      });
    } else {
      // For dynamic repeater: show NO fields when no API data
      this.clearAllItems();
      this.debugLog(
        'No API data - no template fields shown (dynamic behavior)'
      );
    }

    this.cd.detectChanges();
  }

  /**
   * Process items data and populate repeater items
   */
  private processItemsData(): void {
    if (!Array.isArray(this.itemsData)) {
      this.debugLog('No valid items array found', this.itemsData);
      return;
    }

    // Clear existing items
    this.clearAllItems();

    // Add items based on data
    this.itemsData.forEach((itemData, index) => {
      this.add();
      this.setInitialValuesForItem(index, itemData);
      this.updateFieldLabelsWithPopulatedData(index, itemData);
    });

    this.debugLog('Items data processed', {
      itemCount: this.itemsData.length,
      items: this.itemsData,
    });

    // Force set values after a short delay to ensure form controls are ready
    setTimeout(() => {
      this.forceSetAllItemIds();
    }, 100);

    this.cd.detectChanges();
  }

  /**
   * Force set all item IDs after form controls are fully initialized
   * This ensures values don't get cleared by Formly's internal processing
   */
  private forceSetAllItemIds(): void {
    this.debugLog('=== FORCE SETTING ALL ITEM IDS ===');

    for (
      let i = 0;
      i < this.formControl.length && i < this.itemsData.length;
      i++
    ) {
      const formGroup = this.formControl.at(i);
      const itemData = this.itemsData[i];
      const extractedId = this.getItemValue(itemData, this.config.itemIdField!);

      if (formGroup && extractedId) {
        // Multiple approaches to ensure the value sticks
        const itemIdControl = formGroup.get('itemId');

        if (itemIdControl) {
          // Method 1: Direct setValue
          itemIdControl.setValue(extractedId);

          // Method 2: PatchValue on the group
          formGroup.patchValue({ itemId: extractedId });

          // Method 3: Mark as touched to ensure it's processed
          itemIdControl.markAsTouched();
          itemIdControl.markAsDirty();

          this.debugLog(`Forced set itemId for index ${i}`, {
            index: i,
            extractedId,
            controlValue: itemIdControl.value,
            formGroupValue: formGroup.value,
          });
        } else {
          this.debugLog(`No itemId control found for index ${i}`, {
            index: i,
            formGroupControls: Object.keys(formGroup),
          });
        }
      }
    }

    this.debugLog('=== FORCE SET COMPLETE ===');
  }

  /**
   * Clear all items from the repeater
   */
  private clearAllItems(): void {
    while (this.formControl.length > 0) {
      this.formControl.removeAt(0);
    }

    if (this.field.fieldGroup) {
      this.field.fieldGroup.length = 0;
    }

    this.debugLog('All items cleared');
  }

  /**
   * Set initial values for a specific item including hidden fields
   * Mark these as API-populated to distinguish from user input
   */
  private setInitialValuesForItem(index: number, itemData: any): void {
    const formGroup = this.formControl.at(index);
    if (!formGroup) {
      this.debugLog('FormGroup not found for index', index);
      return;
    }

    // Extract the actual ID value using the configured path
    const extractedId = this.getItemValue(itemData, this.config.itemIdField!);

    this.debugLog('BEFORE setting values', {
      index,
      itemData,
      configuredIdPath: this.config.itemIdField,
      extractedIdValue: extractedId,
      formGroupValue: formGroup.value,
      itemDataKeys: Object.keys(itemData || {}),
    });

    // Set hidden field values - only store the ID
    const hiddenFieldValues = {
      itemId: extractedId, // Store the actual ID value
      _isApiPopulated: true, // Flag to track API-populated items
    };

    // Patch the form control with hidden field values
    formGroup.patchValue(hiddenFieldValues);

    // Force the value to be set immediately
    if (formGroup.get('itemId')) {
      formGroup.get('itemId')?.setValue(extractedId);
    }

    // Mark as pristine since this is API data, not user input
    formGroup.markAsPristine();

    this.debugLog('AFTER setting values', {
      index,
      extractedIdValue: extractedId,
      hiddenFieldValues,
      formGroupValue: formGroup.value,
      itemIdControlValue: formGroup.get('itemId')?.value,
    });
  }

  /**
   * Update field labels with populated data for dynamic interpolation
   */
  private updateFieldLabelsWithPopulatedData(
    index: number,
    itemData: any
  ): void {
    const fieldGroup = this.field.fieldGroup?.[index];
    if (!fieldGroup?.fieldGroup) {
      return;
    }

    const itemLabel = this.getItemValue(itemData, this.config.itemLabelField!);
    const itemId = this.getItemValue(itemData, this.config.itemIdField!);

    // Update labels in all fields within this group
    this.updateLabelsRecursively(fieldGroup.fieldGroup, {
      itemLabel,
      itemId,
      label: itemLabel,
      id: itemId,
    });

    this.debugLog('Updated field labels for item', {
      index,
      itemLabel,
      itemId,
      itemData,
      itemIdFromPath: this.config.itemIdField,
      itemLabelFromPath: this.config.itemLabelField,
    });
  }

  /**
   * Recursively update labels in field groups
   */
  private updateLabelsRecursively(
    fields: FormlyFieldConfig[],
    replacements: Record<string, any>
  ): void {
    fields.forEach(field => {
      if (field.props?.label && typeof field.props.label === 'string') {
        // Replace template variables in label
        field.props.label = field.props.label.replace(
          /\{\{(\w+)\}\}/g,
          (match, fieldName) => {
            return replacements[fieldName] || match;
          }
        );
      }

      if (
        field.props?.placeholder &&
        typeof field.props.placeholder === 'string'
      ) {
        // Replace template variables in placeholder
        field.props.placeholder = field.props.placeholder.replace(
          /\{\{(\w+)\}\}/g,
          (match, fieldName) => {
            return replacements[fieldName] || match;
          }
        );
      }

      if (field.props && typeof field.props['hint'] === 'string') {
        // Replace template variables in hint
        field.props['hint'] = field.props['hint'].replace(
          /\{\{(\w+)\}\}/g,
          (match, fieldName) => {
            return replacements[fieldName] || match;
          }
        );
      }

      // Recursively process nested field groups
      if (field.fieldGroup) {
        this.updateLabelsRecursively(field.fieldGroup, replacements);
      }
    });
  }

  /**
   * Get value from item data using dot notation
   */
  private getItemValue(itemData: any, fieldPath: string): any {
    if (!itemData || !fieldPath) {
      this.debugLog('getItemValue: Invalid input', {
        itemData,
        fieldPath,
        itemDataType: typeof itemData,
      });
      return undefined;
    }

    const result = fieldPath.split('.').reduce((obj, key) => {
      this.debugLog('getItemValue: Processing key', {
        currentObj: obj,
        key,
        hasKey: obj && Object.prototype.hasOwnProperty.call(obj, key),
        value: obj?.[key],
      });
      return obj?.[key];
    }, itemData);

    this.debugLog('getItemValue: Final result', {
      fieldPath,
      itemData,
      result,
      resultType: typeof result,
    });

    return result;
  }

  /**
   * Generate item header text from template
   * Uses the item label from API data directly
   */
  getItemHeader(index: number): string {
    // Get fresh values from API data for this index
    if (index < this.itemsData.length) {
      const itemData = this.itemsData[index];
      const itemLabel = this.getItemValue(
        itemData,
        this.config.itemLabelField!
      );

      if (itemLabel) {
        return itemLabel;
      }
    }

    // Fall back to generic header
    return `Item ${index + 1}`;
  }

  /**
   * Check if there are any items to display
   */
  hasItems(): boolean {
    return !!(this.field.fieldGroup && this.field.fieldGroup.length > 0);
  }

  /**
   * Get translated empty state message
   */
  getEmptyStateMessage(): string {
    return this.translateService.instant(this.config.emptyStateMessage!);
  }

  /**
   * Override the value getter to exclude API-populated items that haven't been modified
   * This ensures only user-input data is included in form serialization
   */
  get value(): any[] {
    if (!this.formControl || this.formControl.length === 0) {
      return [];
    }

    const result: any[] = [];

    for (let i = 0; i < this.formControl.length; i++) {
      const control = this.formControl.at(i);
      const controlValue = control.value;

      // Skip items that are API-populated and haven't been modified by user
      if (this.isApiPopulatedAndUnmodified(control, controlValue)) {
        this.debugLog(
          'Excluding API-populated unmodified item from form value',
          {
            index: i,
            value: controlValue,
          }
        );
        continue;
      }

      // Include items that have user input (cleaned of API flags)
      const cleanValue = this.cleanApiFlags(controlValue);
      result.push(cleanValue);
    }

    this.debugLog('Dynamic repeater final form value', {
      totalItems: this.formControl.length,
      includedItems: result.length,
      result,
    });

    return result;
  }

  /**
   * Check if an item is API-populated and hasn't been modified by user
   */
  private isApiPopulatedAndUnmodified(control: any, value: any): boolean {
    // If not flagged as API-populated, include it (user-added)
    if (!value._isApiPopulated) {
      return false;
    }

    // If the control is dirty, user has modified it
    if (control.dirty) {
      return false;
    }

    // Check if user has filled any visible fields
    const hasUserInput = Object.keys(value).some(key => {
      // Skip internal tracking fields
      if (key === 'itemId' || key === '_isApiPopulated') {
        return false;
      }

      // Check if field has actual user input
      const fieldValue = value[key];
      return (
        fieldValue !== null && fieldValue !== undefined && fieldValue !== ''
      );
    });

    // If API-populated and no user input, exclude from form value
    return !hasUserInput;
  }

  /**
   * Remove internal API tracking flags from the value
   */
  private cleanApiFlags(value: any): any {
    const cleaned = { ...value };
    delete cleaned._isApiPopulated;
    return cleaned;
  }

  /**
   * Debug logging utility
   */
  private debugLog(message: string, data?: any): void {
    void message;
    void data;
    if (this.config.enableDebugLogging) {
      // DEBUGGING...
    }
  }

  /**
   * Debug method to check current form values - for testing
   */
  debugCurrentFormValues(): void {
    console.log('=== CURRENT FORM VALUES DEBUG ===');
    console.log('Form Control Length:', this.formControl.length);

    for (let i = 0; i < this.formControl.length; i++) {
      const control = this.formControl.at(i);
      console.log(`Item ${i}:`, {
        value: control.value,
        itemIdControl: control.get('itemId')?.value,
        dirty: control.dirty,
        pristine: control.pristine,
      });
    }

    console.log('Component Value Getter:', this.value);
    console.log('==================================');
  }
}
